// offlineDB.js - Handles IndexedDB operations for offline data
export const DB_NAME = 'prestage-offline-db';
export const DB_VERSION = 2; // Incrementing the version number to trigger database upgrade
export const CONVENTIONS_STORE = 'conventions';
export const USER_STORE = 'user-data';

// Open database connection
export function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject(event.target.error);
    };
    
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object stores when the database is first created or version is upgraded
      if (!db.objectStoreNames.contains(CONVENTIONS_STORE)) {
        db.createObjectStore(CONVENTIONS_STORE, { keyPath: 'timestamp' });
        console.log('Created conventions store in IndexedDB');
      }
      
      // Create user data store if it doesn't exist
      if (!db.objectStoreNames.contains(USER_STORE)) {
        db.createObjectStore(USER_STORE, { keyPath: 'id' });
        console.log('Created user data store in IndexedDB');
      }
    };
  });
}

// Save conventions data to IndexedDB - Fixed to handle transaction correctly
export async function saveConventionsData(data) {
  try {
    const db = await openDB();
    
    // Create a single transaction for both operations
    const transaction = db.transaction(CONVENTIONS_STORE, 'readwrite');
    const store = transaction.objectStore(CONVENTIONS_STORE);
    
    // Return a promise that resolves when the transaction is complete
    return new Promise((resolve, reject) => {
      // First clear the store
      const clearRequest = store.clear();
      
      clearRequest.onerror = (event) => {
        console.error('Error clearing store:', event.target.error);
        reject(event.target.error);
      };
      
      clearRequest.onsuccess = () => {
        // After clearing, add the new data in the same transaction
        const record = {
          timestamp: Date.now(),
          data: data
        };
        
        const addRequest = store.add(record);
        
        addRequest.onerror = (event) => {
          console.error('Error saving conventions data:', event.target.error);
          reject(event.target.error);
        };
        
        addRequest.onsuccess = () => {
          console.log('Conventions data saved to IndexedDB');
          resolve(true);
        };
      };
      
      // Listen for transaction completion
      transaction.oncomplete = () => {
        console.log('Transaction completed successfully');
      };
      
      transaction.onerror = (event) => {
        console.error('Transaction failed:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to save conventions data:', error);
    return false;
  }
}

// Get the latest conventions data from IndexedDB
export async function getConventionsData() {
  try {
    const db = await openDB();
    const transaction = db.transaction(CONVENTIONS_STORE, 'readonly');
    const store = transaction.objectStore(CONVENTIONS_STORE);
    
    // Use IDBKeyRange to get the latest entry
    const request = store.openCursor(null, 'prev');
    
    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          resolve(cursor.value);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = (event) => {
        console.error('Error retrieving conventions data:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to get conventions data:', error);
    return null;
  }
}

// Save user data to IndexedDB
export async function saveUserData(userData) {
  try {
    const db = await openDB();
    const transaction = db.transaction(USER_STORE, 'readwrite');
    const store = transaction.objectStore(USER_STORE);
    
    return new Promise((resolve, reject) => {
      // Store with fixed ID to always have single current user record
      const record = {
        id: 'currentUser',
        data: userData,
        timestamp: Date.now()
      };
      
      const addRequest = store.put(record); // use put instead of add to overwrite existing
      
      addRequest.onerror = (event) => {
        console.error('Error saving user data:', event.target.error);
        reject(event.target.error);
      };
      
      addRequest.onsuccess = () => {
        console.log('User data saved to IndexedDB');
        resolve(true);
      };
    });
  } catch (error) {
    console.error('Failed to save user data:', error);
    return false;
  }
}

// Get user data from IndexedDB
export async function getUserData() {
  try {
    const db = await openDB();
    const transaction = db.transaction(USER_STORE, 'readonly');
    const store = transaction.objectStore(USER_STORE);
    
    const request = store.get('currentUser');
    
    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        if (request.result) {
          resolve(request.result.data);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = (event) => {
        console.error('Error retrieving user data:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to get user data:', error);
    return null;
  }
}

// Check if the current user is an admin
export async function isUserAdmin() {
  try {
    const userData = await getUserData();
    return userData && userData.isAdmin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}